import React, { useContext } from 'react';
import { useAuth } from '@jutro/auth';
import { SecureRoute } from '@jutro/router';
import { WMICLoader } from 'gw-components-platform-react';
import { useAuthentication } from './AuthenticationContext';

/**
 * Wraps a component in a secure route with an authentication context
 *
 * @alias privateRouteWithAuthContext
 * @memberof module:gw-digital-auth-react
 *
 * @param {ReactElement} WrappedComponent a component to be wrapped
 *
 * @returns {ReactElement} the element wrapped in a secure route with an authentication context
 */
const privateRouteWithAuthContext = WrappedComponent => {
    return function ContextComponent(...otherProps) {
        const { isAuthenticated, isPending } = useAuth();
        const { authHeader } = useAuthentication();

        const hasToken = Boolean(authHeader && authHeader.Authorization);
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        // [WGW-5485] HACK — REMOVE WHEN PROPER FIX IS IN PLACE.
        // Safari ITP loses OIDC token state after Okta redirect, leaving
        // isAuthenticated=false even though authHeader was briefly set in
        // React context. A single page reload recovers the session (the
        // tokens are reacquired from the Okta session on second load).
        // sessionStorage flag prevents infinite reload loop.
        // Proper fix: configure Okta SDK tokenManager.storage to 'memory'
        // or fix restoreSession() handling in @jutro/auth OidcSession.
        if (isSafari && !isPending && !isAuthenticated && hasToken) {
            const reloadKey = 'wgw5485_safari_reload';
            if (!sessionStorage.getItem(reloadKey)) {
                sessionStorage.setItem(reloadKey, '1');
                window.location.reload();
                return null;
            }
            sessionStorage.removeItem(reloadKey);
        }

        if (isAuthenticated) {
            sessionStorage.removeItem('wgw5485_safari_reload');
        }

        return (
            <SecureRoute
                {...otherProps}
                render={props =>
                    !isPending ? (
                        isAuthenticated ? (
                            <WrappedComponent {...props} />
                        ) : hasToken ? (
                            <WMICLoader />
                        ) : null
                    ) : (
                        <WMICLoader />
                    )
                }
            />
        );
    };
};

export default privateRouteWithAuthContext;
